/**
 * Ticker suggestions for the Tickers box, from lists the builder has already
 * loaded.
 *
 * Nothing is fetched: the Newsquawk library alone carries the S&P 500's 444
 * symbols, and the user's own lists add the names they follow. A symbol held
 * by more lists is more likely the one meant, so it ranks first. Suggestions
 * only offer; what was typed goes in as typed (see classifyTickerEntry). With
 * content-hub the source can become the tickers actually seen on stories.
 *
 * Its own module, importing nothing, so the rules can be tested in node.
 */

export interface TickerIndex {
  /** Every symbol, most-listed first. */
  entries: { symbol: string; lists: number }[];
  known: Set<string>;
}

const EMPTY: TickerIndex = { entries: [], known: new Set() };

/** Indexes the tickers of every list given, counting each list once. */
export function buildTickerIndex(lists: { tickers?: string[] }[]): TickerIndex {
  const counts = new Map<string, number>();
  for (const list of lists) {
    const symbols = new Set(
      (list.tickers ?? [])
        .map((t) => t.trim().toUpperCase())
        .filter((t) => t.length > 0)
    );
    for (const symbol of symbols) counts.set(symbol, (counts.get(symbol) ?? 0) + 1);
  }
  if (counts.size === 0) return EMPTY;
  const entries = [...counts.entries()]
    .map(([symbol, lists]) => ({ symbol, lists }))
    .sort(
      (a, b) =>
        b.lists - a.lists ||
        a.symbol.length - b.symbol.length ||
        a.symbol.localeCompare(b.symbol)
    );
  return { entries, known: new Set(counts.keys()) };
}

export function isKnownTicker(index: TickerIndex, symbol: string): boolean {
  return index.known.has(symbol.trim().toUpperCase());
}

/**
 * Up to `limit` symbols starting with what's typed, most-listed first. The
 * symbol itself is left out once typed in full — suggesting it back is
 * noise — and so is anything already in the box.
 */
export function suggestTickers(
  index: TickerIndex,
  typed: string,
  options: { exclude?: Iterable<string>; limit?: number } = {}
): string[] {
  const prefix = typed.trim().replace(/^\$/, "").toUpperCase();
  if (!prefix) return [];
  const exclude = new Set(options.exclude ?? []);
  const limit = options.limit ?? 5;
  const out: string[] = [];
  for (const { symbol } of index.entries) {
    if (symbol === prefix || exclude.has(symbol) || !symbol.startsWith(prefix)) continue;
    out.push(symbol);
    if (out.length >= limit) break;
  }
  return out;
}
